import React, { useContext, useEffect, useMemo, useRef } from 'react';
import ServerContext from '../../../contexts/serverContext';
import './subcategory-page-view.scss';
import { useSearchContent } from '../../../components/sections/search-bar/hooks/use-search-content';
import { Pagination } from '../../../components/common/pagination/pagination';
import { ASTUTE } from '../../support-constants';
import { FdsChevron } from '../../../components/common/chevron/fds-chevron';
import { useBbtContent } from '../browse-by-topic/hook/use-bbt-content';
import { useAppInformationContent } from '../../../components/sections/breadcrumbs/hook/use-breadcrumb-content';
import BbtCategories from '../bbt-categories/bbt-categories';
import { Topic } from '../../models/topic';
import { useParams } from 'react-router-dom';
import AstuteService from '../../services/astute-service/astute-service';
import { DiscoverabilityPageLoad } from '../../../services/shortcode-service/shortcode-service';
import { useAnalytics } from '../../../hooks/use-analytics';
import useSeoConfig from '../../../hooks/use-seo-config';
import BreadcrumbsAndViewBanner from '../breadcrumbs-and-view-banner/breadcrumbs-and-view-banner';
import { CatSubcatFilteredResponse } from '../../models/cat-subcat-filtered-response';
import { enableLanguageLinksforTH } from '../../../components/utils/syndicate-util/syndicate-util';
import {
    buildBreadcrumbListSchema,
    getBbtErrorMessageForSubCategoryPage,
    getCategoryNameFromCategoriesResponse,
    getCurrentRootFromServerContext,
} from '../astute-topic/util/astute-topic-util';
import { CategoryAndSubCategoryPageBreadCrumb } from './categoryAndSubCategoryPageBreadCrumb';
import siteMapService from '../../services/site-map-service/site-map-service';
import { SlackMessages } from '../../../services/slack-message-service/SlackMessageService';
import { useServerData } from '../../../hooks/use-server-data';
import { ActivityIndicator } from '../../../components/common/activity-indicator/activity-indicator';

interface SubCategoryPageParams {
    category: string;
    subcategory: string;
}

const SubCategoryPageView = () => {
    const { category, subcategory } = useParams<SubCategoryPageParams>();
    const searchContent = useSearchContent();
    const bbtContent = useBbtContent();
    const serverContext = useContext(ServerContext);
    const homePagetitle = useAppInformationContent();
    const currentRoot = getCurrentRootFromServerContext(serverContext);
    const domainRoot = `https://${serverContext.domain}${currentRoot}`;
    const astuteServiceRef = useRef(new AstuteService());
    
    const fetchSubcatData = async (): Promise<CatSubcatFilteredResponse & { error?: string }> => {
        try {
            const result = await astuteServiceRef.current.getAllFilteredCatAndSubcatV2(
                serverContext.currentLanguageRegionCode,
                serverContext.brand,
                category
            );
            const filteredCategories = result.filteredCategories;
            const subCat = filteredCategories?.subCategories?.find(
                sc => sc.subCategorySeo === subcategory
            );
            if (subCat) {
                serverContext.metaDescription = subCat.subCategoryName;
                serverContext.documentTitle = subCat.subCategoryName;
                if (subCat.hrefLangs) {
                    serverContext.hrefLangs = subCat.hrefLangs;
                }
            }
            return { filteredCategories, categories: result.categories, error: '' };
        } catch (e) {
            return { error: `SubCategory Page Error: ${e}` };
        }
    };

    const [subcatData] = useServerData<CatSubcatFilteredResponse & { error?: string }>('subcatPage', fetchSubcatData);
    const activeSubCategory = useMemo(() =>
        subcatData?.data?.filteredCategories?.subCategories?.find(
            subCat => subCat.subCategorySeo === subcategory
        ),
        [subcatData, subcategory]
    );

    useEffect(() => {
        if (activeSubCategory) {
            serverContext.metaDescription = activeSubCategory.subCategoryName;
            serverContext.documentTitle = activeSubCategory.subCategoryName;
            if (activeSubCategory.hrefLangs) {
                serverContext.hrefLangs = activeSubCategory.hrefLangs;
            }
        }
    }, [activeSubCategory]);

    useSeoConfig('bbt-subcategory', 'bbt-subcategory', [
        {
            fieldName: 'category',
            fieldValue: getCategoryNameFromCategoriesResponse(
                subcatData
            ),
        },
        {
            fieldName: 'sub-category',
            fieldValue: activeSubCategory?.subCategoryName || '',
        },
    ]);

    const redirectionForSubCategoryView = async (errorMessage: string) => {
        const categoryPath = siteMapService.getPathFromRouterConfigs(
            serverContext.currentLanguageRegionCode,
            serverContext.brand,
            'CategoryPageView',
            [category]
        );
        if (errorMessage === SlackMessages.BBT_CONTENT_DISABLED) {
            window.location.href = `${currentRoot}/`;
        } else if (
            errorMessage === SlackMessages.SUBCATEGORY_PAGE_INVALID_URL
        ) {
            window.location.href = `${currentRoot}${categoryPath}/`;
        } else {
            window.location.href = `${currentRoot}#bbt`;
        }
    };

    useEffect(() => {
        if (!bbtContent || subcatData === null) return;
        const enriched = {
            data: { ...subcatData.data, filteredSubCategories: activeSubCategory },
        };
        const errorMessage = getBbtErrorMessageForSubCategoryPage(
            bbtContent,
            enriched
        );
        errorMessage && redirectionForSubCategoryView(errorMessage);
    }, [bbtContent, subcatData, activeSubCategory]);

    const discoverabilityPageLoad: DiscoverabilityPageLoad = {
        category: category?.replaceAll('-', ' '),
        subcategory: subcategory?.replaceAll('-', ' '),
    };
    const [fireEvents] = useAnalytics();
    useEffect(() => {
        enableLanguageLinksforTH();
        fireEvents(
            ['self-help-subcategory'],
            undefined,
            {
                discoverabilityPageLoad: discoverabilityPageLoad,
            },
            false
        );
    }, []);

    const subCategoryBreadcrumbScript =
        bbtContent &&
        !bbtContent.hideBrowseByTopic &&
        buildBreadcrumbListSchema([
            { id: domainRoot, name: homePagetitle?.applicationName },
            {
                id: `${domainRoot}${subcatData?.data?.filteredCategories?.categoryAbsoluteSeo}`,
                name: subcatData?.data?.filteredCategories?.categoryName,
            },
            {
                id: `${domainRoot}${activeSubCategory?.subCategoryAbsoluteSeo}`,
                name: activeSubCategory?.subCategoryName,
            },
        ]);

    const topicList =
        activeSubCategory &&
        activeSubCategory.allTopics?.map(
            (topic: Topic) => {
                return {
                    id: topic.topicId,
                    label: topic.topicLabel,
                    link: topic.topicAbsoluteSeo,
                };
            }
        );

    if (!searchContent || !bbtContent || bbtContent.hideBrowseByTopic) {
        return null;
    }

    if (!subcatData?.data) {
        return (
            <div style={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ActivityIndicator/>
            </div>
        );
    }

    return (
        <div className="subcategorypage">
            <BreadcrumbsAndViewBanner
                currentPage={
                    activeSubCategory?.subCategoryName || ''
                }
                hashId={'#bbt'}
                previousCrumb={
                    subcategory && (
                        <CategoryAndSubCategoryPageBreadCrumb
                            categoriesResponse={subcatData}
                            currentRoot={currentRoot}
                        />
                    )
                }
                bbtTitle={bbtContent.title}
                bbtSubtitle={bbtContent.subTitle}
                isCatPage={true}
                searchClassName="sh-search"
            />

            <div className="browse-by-topic">
                <div
                    dangerouslySetInnerHTML={{
                        __html: subCategoryBreadcrumbScript || '',
                    }}
                />
                {subcatData?.data?.categories && (
                    <BbtCategories
                        category={category}
                        subcategory={subcategory}
                        categories={subcatData.data.categories}
                        filteredCategories={
                            subcatData?.data?.filteredCategories
                        }
                        bbtContent={bbtContent}
                    />
                )}
                <div className="verticalline"></div>
                <section className="topics">
                    <div className="subcategory-title">
                        <div className="mobile-title">
                            {subcatData?.data?.filteredCategories?.categoryName && activeSubCategory?.subCategoryName ? (
                                <a
                                    className="title-link"
                                    href={`${currentRoot}${subcatData?.data?.filteredCategories?.categoryAbsoluteSeo}`}
                                    aria-label={subcatData?.data?.filteredCategories?.categoryName}
                                >
                                    <FdsChevron
                                        className="chevron"
                                        type="unfilled"
                                        direction="left"
                                    ></FdsChevron>
                                    <span className="subcategory-link-text">
                                        {activeSubCategory?.subCategoryName}
                                    </span>
                                </a>
                            ) : null}
                        </div>
                        {activeSubCategory?.subCategoryName ? (
                            <h2 className="desktop-title">
                                {activeSubCategory?.subCategoryName.toUpperCase()}
                            </h2>
                        ) : null}
                    </div>
                    {topicList && topicList.length > 0 && (
                        <Pagination
                            key={subcategory}
                            data={topicList}
                            itemsPerPage={ASTUTE.PAGINATION_ITEMS_PER_PAGE}
                        />
                    )}
                </section>
            </div>
        </div>
    );
};

export default SubCategoryPageView;
